'use client';
import { useCallback, useEffect, useRef, useState } from 'react';

/* ============================================================
   LeagueSetup — "connect your Sleeper account" (§5.4).

   Sleeper has no OAuth for the public read API, so "connecting" is really
   two paths to a league_id:

     A) paste a league ID (or the URL you copied out of Sleeper's web app —
        we pull the ID out of it)
     B) type your Sleeper USERNAME, and we look up your leagues for the
        current season and let you pick one. This is the path almost
        everyone will actually use; nobody knows their league ID.

   Two extra endpoints beyond §7's table, both public and unauthenticated:
     /user/<username>                    -> { user_id, ... }
     /user/<user_id>/leagues/nfl/<season> -> [ { league_id, name, ... } ]

   Persistence is localStorage under one key. The hook is exported
   separately (useLeagueId) so the app can read the stored ID without
   mounting the UI.
   ============================================================ */

const BASE = 'https://api.sleeper.app/v1';
const STORAGE_KEY = 'solari.sleeper.leagueId';

const AMBER = '#ffb300';
const DIM = '#5c6068';

// Accept a bare ID, or any Sleeper URL containing /leagues/<id>/ or
// /league/<id>. League IDs are long numeric strings.
export function parseLeagueId(input) {
  if (!input) return null;
  const s = String(input).trim();
  if (/^\d{6,}$/.test(s)) return s;
  const m = s.match(/(?:leagues?)\/(\d{6,})/i) || s.match(/(\d{12,})/);
  return m ? m[1] : null;
}

export function useLeagueId() {
  const [leagueId, setLeagueIdState] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      setLeagueIdState(window.localStorage.getItem(STORAGE_KEY));
    } catch {
      /* storage blocked (private mode / embedded) — run without persistence */
    }
    setReady(true);
  }, []);

  const setLeagueId = useCallback((id) => {
    setLeagueIdState(id);
    try {
      if (id) window.localStorage.setItem(STORAGE_KEY, id);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch { /* non-fatal */ }
  }, []);

  return { leagueId, setLeagueId, ready };
}

async function getJSON(url, fetchImpl) {
  const res = await fetchImpl(url);
  if (!res.ok) throw new Error(`Sleeper ${res.status}`);
  return res.json();
}

export async function lookupLeaguesByUsername(username, season, fetchImpl = fetch) {
  const user = await getJSON(`${BASE}/user/${encodeURIComponent(username.trim())}`, fetchImpl);
  if (!user || !user.user_id) throw new Error('No Sleeper user by that name.');
  const leagues = await getJSON(
    `${BASE}/user/${user.user_id}/leagues/nfl/${season}`,
    fetchImpl
  );
  return Array.isArray(leagues) ? leagues : [];
}

export default function LeagueSetup({
  onSelect, season, fetchImpl = fetch,
  header = null,   // rendered above the connect form (landing page masthead)
  footer = null,   // rendered below the hint line (site nav)
  // Arriving via /#sleeper/<username>: prefill the field and run the lookup
  // immediately, so that route costs one tap (pick a league) instead of
  // making someone retype the name they already put in the URL.
  initialUsername = null,
}) {
  const [mode, setMode] = useState('username'); // 'username' | 'id'
  const [value, setValue] = useState(initialUsername || '');
  const [leagues, setLeagues] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const resolvedSeason = season || String(new Date().getFullYear());

  useEffect(() => { inputRef.current?.focus(); }, [mode]);

  const runLookup = useCallback(async (raw) => {
    const name = String(raw || '').trim();
    if (!name) { setError('Enter your Sleeper username.'); return; }
    setError(null);
    setBusy(true);
    try {
      let found = await lookupLeaguesByUsername(name, resolvedSeason, fetchImpl);
      // mid-offseason a user's leagues can live on the prior season
      if (!found.length) {
        found = await lookupLeaguesByUsername(
          name, String(Number(resolvedSeason) - 1), fetchImpl
        );
      }
      if (!found.length) setError('No NFL leagues found for that username.');
      setLeagues(found);
      // One league is not a choice -- go straight to the board.
      if (found.length === 1) onSelect(found[0].league_id);
    } catch (err) {
      setError(err.message || 'Lookup failed.');
    } finally {
      setBusy(false);
    }
  }, [resolvedSeason, fetchImpl, onSelect]);

  // Run the prefilled lookup exactly once, on arrival via /#sleeper/<name>.
  // `ranInitial` is the guard, so runLookup can sit in the dep array
  // honestly -- no eslint-disable needed. (A disable comment for an
  // unregistered rule is itself a CI build failure; see HANDOFF §8.)
  const ranInitial = useRef(false);
  useEffect(() => {
    if (!initialUsername || ranInitial.current) return;
    ranInitial.current = true;
    setMode('username');
    setValue(initialUsername);
    runLookup(initialUsername);
  }, [initialUsername, runLookup]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    setError(null);
    if (mode === 'id') {
      const id = parseLeagueId(value);
      if (!id) { setError('That does not look like a Sleeper league ID or URL.'); return; }
      onSelect(id);
      return;
    }
    runLookup(value);
  }, [mode, value, onSelect, runLookup]);

  return (
    <div style={wrap}>
      {header}
      {/* <h2>, not <h1>: this form is a section of the landing page, whose
          <h1> is the tagline in BoardMasthead (rendered just above via the
          `header` prop). `title` sets fontSize/fontWeight/margin explicitly,
          so this is a semantics-only change -- nothing moves. */}
      <h2 style={title}>CONNECT YOUR LEAGUE</h2>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <Tab active={mode === 'username'} onClick={() => { setMode('username'); setLeagues(null); }}>
          SLEEPER USERNAME
        </Tab>
        <Tab active={mode === 'id'} onClick={() => { setMode('id'); setLeagues(null); }}>
          LEAGUE ID
        </Tab>
      </div>

      <form onSubmit={submit} style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 460 }}>
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={mode === 'username' ? 'your sleeper username' : 'league ID or Sleeper URL'}
          style={input}
          spellCheck={false}
          autoComplete="off"
        />
        <button type="submit" disabled={busy} style={{ ...button, opacity: busy ? 0.5 : 1 }}>
          {busy ? '...' : mode === 'username' ? 'FIND' : 'GO'}
        </button>
      </form>

      {error && <p style={errorText}>{error}</p>}

      {leagues && leagues.length > 0 && (
        <ul style={list}>
          {leagues.map((l) => (
            <li key={l.league_id}>
              <button type="button" onClick={() => onSelect(l.league_id)} style={leagueBtn}>
                <span style={{ color: AMBER }}>{l.name}</span>
                <span style={{ color: DIM, fontSize: 11 }}>
                  {l.total_rosters} teams · {l.season}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p style={hint}>
        Read-only. We only use Sleeper's public API — no password, no account access.
      </p>
      {footer}
    </div>
  );
}

function Tab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...button,
        color: active ? AMBER : DIM,
        borderColor: active ? AMBER : '#2a2d33',
      }}
    >
      {children}
    </button>
  );
}

const wrap = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  justifyContent: 'center', minHeight: '70vh', padding: '5vh 4vw 4vh', gap: 4,
};
const title = {
  color: AMBER, fontSize: 15, letterSpacing: '.3em', fontWeight: 700,
  margin: '0 0 24px',
};
const input = {
  flex: 1, background: '#0d0f12', border: '1px solid #2a2d33', color: '#e8e8e8',
  padding: '12px 14px', fontSize: 14, letterSpacing: '.08em', borderRadius: 2,
  outline: 'none', fontFamily: 'inherit',
};
const button = {
  background: 'transparent', border: '1px solid #2a2d33', color: AMBER,
  padding: '12px 16px', fontSize: 11, letterSpacing: '.2em', fontWeight: 700,
  cursor: 'pointer', borderRadius: 2, fontFamily: 'inherit',
};
const leagueBtn = {
  ...button, width: '100%', display: 'flex', justifyContent: 'space-between',
  alignItems: 'center', gap: 16, textAlign: 'left', padding: '14px 16px',
};
const list = {
  listStyle: 'none', padding: 0, margin: '20px 0 0', width: '100%',
  maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 8,
};
const errorText = { color: '#c9564e', fontSize: 12, letterSpacing: '.1em', marginTop: 14 };
const hint = { color: DIM, fontSize: 11, letterSpacing: '.1em', marginTop: 28, textAlign: 'center' };
